import { Link } from "react-router-dom";
import { Rating, BenefitList, BuyButton } from "@/components/ProductCard";
import { CategoryChips } from "@/components/home/CategorySection";
import { ArrowDown, ShieldCheck, Truck, Star, Lightning } from "@phosphor-icons/react";

const TRUST = [
  { icon: Star, text: "Only 4★+ rated products" },
  { icon: ShieldCheck, text: "Secure checkout on Amazon.in" },
  { icon: Truck, text: "Fast Prime delivery across India" },
];

function HeroPickCard({ product }) {
  return (
    <div data-testid="hero-top-pick" className="relative rounded-3xl bg-white border border-[#E5E0D8] shadow-[0_24px_70px_rgba(44,43,41,0.14)] overflow-hidden">
      <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-1 bg-[#D17A58] text-white text-xs font-bold px-3 py-1 rounded-full">
        <Lightning size={12} weight="fill" /> Today's Top Pick
      </span>
      <Link to={`/product/${product.id}`} className="block aspect-[16/10] sm:aspect-[4/3] overflow-hidden bg-[#EADDC8]/30">
        <img src={product.image_url} alt={product.title} fetchpriority="high" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
      </Link>
      <div className="p-5 sm:p-6">
        <Link to={`/product/${product.id}`} className="font-display text-xl sm:text-2xl leading-snug text-[#2C2B29] hover:text-[#5B7B53]">{product.title}</Link>
        <div className="flex items-center gap-2 mt-2">
          <Rating value={product.rating} />
          <span className="text-xs text-[#5C5A56]">{product.rating} · {product.review_count.toLocaleString()} reviews</span>
        </div>
        <BenefitList items={product.benefits} testid="hero-top-pick-benefits" />
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <span className="text-2xl font-semibold text-[#2C2B29]">{product.price}</span>
          <BuyButton product={product} testid="hero-top-pick-buy" className="max-sm:w-full max-sm:py-3.5" />
        </div>
      </div>
    </div>
  );
}

export function HomeHero({ topPick, categories, productCount }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#EADDC8]/60 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-7 pb-10 lg:pt-20 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-8 items-center">
        <div className="lg:col-span-7 lg:row-start-1 min-w-0 hh-rise">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#5B7B53]/10 text-[#465F41] text-xs font-semibold px-3 py-1.5 mb-4 sm:mb-6">
            {productCount}+ handpicked home & kitchen buys
          </span>
          <h1 className="font-display text-[2.15rem] sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-[#2C2B29]">
            Smarter buys for your <em className="italic text-[#D17A58]">Indian kitchen</em> & home.
          </h1>
          <p className="mt-4 sm:mt-5 text-[15px] sm:text-lg text-[#5C5A56] max-w-xl leading-relaxed">
            Skip hours of scrolling. We shortlist the most useful, best-reviewed products on Amazon.in — with the key benefits up front, so you can buy with confidence.
          </p>
          <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
            <a href="#top-picks" data-testid="hero-see-top-picks" className="hh-btn-primary rounded-full px-4 sm:px-6 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2">
              Top picks <ArrowDown size={16} weight="bold" />
            </a>
            <Link to="/lists" data-testid="hero-explore-lists" className="rounded-full px-4 sm:px-6 py-3 text-sm font-semibold border border-[#2C2B29] text-[#2C2B29] text-center hover:bg-[#2C2B29] hover:text-white transition-colors">
              Buying guides
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2 min-w-0 hh-rise" style={{ animationDelay: "150ms" }}>
          {topPick && <HeroPickCard product={topPick} />}
        </div>
        <div className="lg:col-span-7 lg:row-start-2 min-w-0">
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm text-[#5C5A56]">
            {TRUST.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-1.5"><Icon size={16} weight="duotone" className="text-[#5B7B53]" /> {text}</li>
            ))}
          </ul>
          <div className="mt-6 lg:mt-10"><CategoryChips categories={categories} /></div>
        </div>
      </div>
    </section>
  );
}
